import React from "react";
import { createRoot } from "react-dom/client";
import { PROGRAM_KICKOFF, PROGRAM_WEEKS, phases, resourceLinks, sessionDates, sessionRange, sessions } from "./data";
import Deck from "./Deck";
import { deckMeta, slides as session05Slides } from "./deck-data";
import { session1Meta, session1Slides } from "./deck-session-1";
import { session2Meta, session2Slides } from "./deck-session-2";
import "./styles.css";

const sessionPath = "/session-0-5";
const deckPath = resourceLinks.session05Deck;
const session1DeckPath = resourceLinks.session1Deck;
const session2DeckPath = resourceLinks.session2Deck;

/** Where "open the current session" should send people, per session id. */
const sessionEntry: Record<string, { href: string; label: string }> = {
  "0.5": { href: sessionPath, label: "Open the Session 0.5 study page" },
  "1": { href: session1DeckPath, label: "Open the Session 1 study deck" },
  "2": { href: session2DeckPath, label: "Open the Session 2 study deck" },
};
const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";

/* ---------- Progress math: every state derives from today's date ---------- */

const now = new Date();
const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

type Status = "done" | "current" | "ahead";

function sessionStatus(id: string): Status {
  const { start, end } = sessionRange(id);
  if (todayUTC >= end) return "done";
  if (todayUTC >= start) return "current";
  return "ahead";
}

const doneCount = sessions.filter((s) => sessionStatus(s.id) === "done").length;
const currentSession = sessions.find((s) => sessionStatus(s.id) === "current") ?? null;
const beforeKickoff = todayUTC < PROGRAM_KICKOFF;
const daysToKickoff = Math.max(0, Math.round((PROGRAM_KICKOFF - todayUTC) / 86400000));
const weekOfProgram = beforeKickoff
  ? 0
  : Math.min(PROGRAM_WEEKS, Math.floor((todayUTC - PROGRAM_KICKOFF) / (7 * 86400000)) + 1);

// Meetings always land on the biweekly boundary: every 14 days from Aug 30, all Sundays.
function nextMeetingUTC() {
  let t = PROGRAM_KICKOFF;
  while (t < todayUTC) t += 14 * 86400000;
  return t;
}

function fmtShort(ms: number) {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(ms));
}

const nextMeet = nextMeetingUTC();
const currentEntry = (currentSession && sessionEntry[currentSession.id]) ?? sessionEntry["0.5"];

const sessionReadings = [
  {
    title: "Zoom In: An Introduction to Circuits",
    authors: "Olah et al. · 2020-03",
    description: "The manifesto of the whole series. Features, circuits, and recurring structure — the three foundational claims are made here.",
    href: "https://distill.pub/2020/circuits/zoom-in/",
  },
  {
    title: "Curve Detectors",
    authors: "Cammarata et al. · 2020-06",
    description: "A close look at an interpretable feature family in a vision model. Watch the methodology, not just the conclusions.",
    href: "https://distill.pub/2020/circuits/curve-detectors/",
  },
  {
    title: "Curve Circuits",
    authors: "Cammarata et al. · 2021-01",
    description: "How individual features combine into a larger mechanism, and what reimplementation with handwritten weights means.",
    href: "https://distill.pub/2020/circuits/curve-circuits/",
  },
  {
    title: "Multimodal Neurons in Artificial Neural Networks",
    authors: "Goh et al. · 2021-03",
    description: "Evidence that human-recognizable concepts can exist across modalities — and the direct origin of superposition research.",
    href: "https://distill.pub/2021/multimodal-neurons/",
  },
];

type ResourceRowProps = {
  label: string;
  title: string;
  description: string;
  href: string;
  action: string;
};

function Logo({ href = "/" }: { href?: string }) {
  return (
    <a className="logo-link" href={href} aria-label="NC AI Opsis home — See for yourself">
      <img src="/nc-ai-opsis-2026-08-21.png" alt="NC AI Opsis — See for yourself." />
    </a>
  );
}

function Header({ onSessionPage = false }: { onSessionPage?: boolean }) {
  return (
    <header className="site-header">
      <div className="page-width header-inner">
        <Logo />
        <nav aria-label="Primary navigation">
          {onSessionPage ? (
            <a href="/">Plan overview</a>
          ) : (
            <>
              <a href="#goals">Goals</a>
              <a href="#expectations">How we work</a>
              <a href="#curriculum">Study plan</a>
              <a href="#materials">Materials</a>
            </>
          )}
          <a className="nav-session" href={sessionPath}>Session 0.5</a>
          <a className="nav-session" href={session1DeckPath}>Session 1</a>
          <a className="nav-session" href={session2DeckPath}>Session 2</a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div className="page-width footer-inner">
        <div>
          <strong>NC AI Opsis</strong>
          <span>A mechanistic interpretability study group, working fully in public.</span>
        </div>
        <div>
          <span>Kickoff · 2026.08.30 · 9:00 PM ET</span>
          <span>Next meeting · {fmtShort(nextMeet)} · 9:00 PM ET</span>
        </div>
      </div>
    </footer>
  );
}

function ResourceRow({ label, title, description, href, action }: ResourceRowProps) {
  const isInternal = href.startsWith("/");
  const content = (
    <>
      <span className="resource-label">{label}</span>
      <span className="resource-copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <span className="resource-action">
        {href ? (isInternal ? `${action} →` : `${action} ↗`) : "Link coming soon"}
      </span>
    </>
  );

  if (!href) return <div className="resource-row resource-pending">{content}</div>;

  return isInternal ? (
    <a className="resource-row" href={href}>{content}</a>
  ) : (
    <a className="resource-row" href={href} target="_blank" rel="noreferrer">{content}</a>
  );
}

function ProgressBoard() {
  return (
    <section className="progress-board" aria-label="Plan progress">
      <div className="page-width board-inner">
        <div className="board-stats">
          {beforeKickoff ? (
            <div><span>Kickoff in</span><strong>{daysToKickoff} {daysToKickoff === 1 ? "day" : "days"}</strong></div>
          ) : (
            <div><span>Current session</span><strong>{currentSession ? `S ${currentSession.id}` : "Wrapped"}</strong></div>
          )}
          <div><span>Sessions done</span><strong>{doneCount}<i> / 27</i></strong></div>
          <div><span>Week</span><strong>{weekOfProgram}<i> / {PROGRAM_WEEKS}</i></strong></div>
          <div><span>Next meeting</span><strong>Sun, {fmtShort(nextMeet)}</strong></div>
        </div>
        <div className="board-track" aria-hidden="true">
          {sessions.map((session) => {
            const status = sessionStatus(session.id);
            return (
              <span
                key={session.id}
                className={status === "done" ? "is-done" : status === "current" ? "is-current" : undefined}
                title={`Session ${session.id} · ${session.title}`}
              />
            );
          })}
        </div>
        <p className="board-caption">
          <span className="cap-done"><b /> Done</span>
          <span className="cap-current"><b /> In progress</span>
          <span className="cap-ahead"><b /> Upcoming</span>
          <span>One cell per session · advances automatically with the date</span>
        </p>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <Header />
      <main>
        <section className="page-width home-hero">
          <p className="kicker">NC AI OPSIS · A public study group · 2026–2027</p>
          <h1>Reading the <span className="accent-word">inner circuits</span> of language models</h1>
          <p className="standfirst">
            We are a study group that meets every two weeks. Over 54 weeks we build a toolbox of
            interpretability instruments — probes, patching, SAEs, diffing, attribution graphs, audits —
            and the judgment to know when each one lies. No teacher and no credits — just a public
            schedule, and an artifact worth showing at every meeting.
          </p>
          <div className="hero-links">
            <a className="text-button" href={sessionPath}>Start with Session 0.5</a>
            <a href="#curriculum">See the full study plan</a>
          </div>
        </section>

        <ProgressBoard />

        <section className="page-width prose-section" id="goals">
          <div className="section-label">Group goals</div>
          <div className="section-body">
            <h2>From reading other people’s claims to building instruments you can trust.</h2>
            <ol className="goal-list">
              <li><span>01</span><div><strong>Build a trusted instrument box.</strong><p>For every technique — probe, patch, steer, diff, graph, agent audit — know what it measures, when it fails, what it costs, and whether causal intervention has validated it.</p></div></li>
              <li><span>02</span><div><strong>Hold one acceptance line all year.</strong><p>Does an intervention change behavior the way the instrument predicted? Reimplementing a circuit by hand was the old bar; this is the new one.</p></div></li>
              <li><span>03</span><div><strong>Follow one thread to the end.</strong><p>What do post-training and RL actually change, and is the reasoning a model says out loud the same as the cognition inside it? Tested in Sessions 15, 21, 22, and 26.</p></div></li>
              <li><span>04</span><div><strong>Audit the AI that does the work.</strong><p>We design the experiments and Claude Code implements them. Our training is writing acceptance criteria and catching the AI’s mistakes and shortcuts — the same muscle a model audit needs.</p></div></li>
            </ol>
          </div>
        </section>

        <section className="page-width prose-section" id="expectations">
          <div className="section-label">How we work</div>
          <div className="section-body">
            <h2>This is a hands-on study group, not a lecture series.</h2>
            <div className="expectation-grid">
              <div><strong>10–17 hours</strong><p>Each session is a two-week box: about 4–7 hours of reading and 6–14 hours of experiments — design, hand-off to the AI, and review of what came back.</p></div>
              <div><strong>Bring an artifact</strong><p>Every meeting needs a notebook, a plot, a circuit diagram, an instrument card, or a short written synthesis.</p></div>
              <div><strong>Log what the AI got wrong</strong><p>Hand-writing every line is no longer the goal. Each session records one line: what the AI did wrong or worked around, and how we caught it.</p></div>
              <div><strong>Explain it your own way</strong><p>Come to each meeting ready to defend one claim and name one unresolved question.</p></div>
            </div>
            <p className="prerequisite-note"><strong>Prerequisites:</strong> Python, PyTorch, linear algebra, probability, and the willingness to read technical papers closely. No interpretability experience required.</p>
            <h3 className="mechanisms-heading">Four mechanisms keep the year honest</h3>
            <ol className="goal-list">
              <li><span>01</span><div><strong>Instrument cards.</strong><p>Every instrument we learn gets a card: what it measures, where it applies, its causal evidence, known failure conditions, cost. The cards become the Instrument Handbook we deliver at the end.</p></div></li>
              <li><span>02</span><div><strong>A claims ledger.</strong><p>One table of what we currently believe interpretability can and cannot do, with each claim marked supported, rejected, or untested and updated every session. It keeps paper titles from steering us.</p></div></li>
              <li><span>03</span><div><strong>A triage agent, and a reshuffle every seven sessions.</strong><p>Three days before each meeting an agent scans the sources and writes a three-line card per new paper. After Sessions 7, 14, and 21 we re-plan for 30 minutes. Swap, never add; the finish date does not move.</p></div></li>
              <li><span>04</span><div><strong>Methods over results.</strong><p>Findings about a specific model expire in months; the instrument primitives — probe, patch, steer, diff, graph, agent audit — have barely changed in four years. A new paper is asked first: new instrument, or new case study?</p></div></li>
            </ol>
          </div>
        </section>

        <section className="current-session">
          <div className="page-width current-session-grid">
            <div className="section-label">Where we are</div>
            <div>
              <p className="session-date">
                {beforeKickoff
                  ? `Kickoff Aug 30 · ${daysToKickoff} ${daysToKickoff === 1 ? "day" : "days"} to go`
                  : currentSession
                    ? sessionDates(currentSession.id)
                    : "The 54-week plan is complete"}
              </p>
              <h2>Session {currentSession?.id ?? "0.5"} · {currentSession?.title ?? "Visual Circuits: Three Foundational Claims"}</h2>
              <p>
                {beforeKickoff
                  ? "The two-week prelude before kickoff: start with the three foundational claims — features, circuits, universality — before moving from vision models to transformers."
                  : currentSession?.focus ?? "Look back over the year's artifacts and compile the synthesis and open questions."}
              </p>
              <a className="text-button light-button" href={currentEntry.href}>{currentEntry.label}</a>
            </div>
          </div>
        </section>

        <section className="page-width curriculum-section" id="curriculum">
          <div className="section-intro">
            <p className="section-label">Study plan</p>
            <div>
              <h2>54 weeks · 27 sessions · six phases</h2>
              <p>This is the schedule we set for ourselves, not a syllabus: nobody lectures, and every session closes with a reproducible artifact. Revised in September 2026 (plan v2): Session 0.5 and Phase I are unchanged, and the second half was rebuilt around instruments, diffing, and audits. New papers can swap into a matching session, but nothing gets added and the meeting dates don’t move.</p>
            </div>
          </div>
          <div className="curriculum-list">
            {phases.map((phase) => {
              const phaseSessions = sessions.filter((session) => session.phase === phase.id);
              return (
                <section className="phase-section" key={phase.id}>
                  <div className="phase-heading">
                    <span>Phase {phase.id}</span>
                    <strong>{phase.label}</strong>
                    <small>{phase.range}</small>
                  </div>
                  <div>
                    {phaseSessions.map((session) => {
                      const status = sessionStatus(session.id);
                      const rowClass = `curriculum-row${status === "done" ? " is-done" : status === "current" ? " is-current" : ""}`;
                      return (
                        <div className={rowClass} key={session.id}>
                          <span className="curriculum-number">{session.id}</span>
                          <span className="curriculum-title">
                            <strong>
                              {sessionEntry[session.id] ? (
                                <a className="title-link" href={sessionEntry[session.id].href}>{session.title}</a>
                              ) : (
                                session.title
                              )}
                              {status === "current" ? <em className="status-tag">In progress</em> : null}
                              {status === "done" ? <em className="status-tag">Done</em> : null}
                            </strong>
                            <small>{session.focus}</small>
                            <span className="curriculum-readings">
                              {session.id === "0.5" ? (
                                <a className="readings-page-link" href={sessionPath}>Study page & annotated readings →</a>
                              ) : null}
                              {session.id === "1" ? (
                                <a className="readings-page-link" href={session1DeckPath}>Study deck →</a>
                              ) : null}
                              {session.id === "1" ? (
                                <a href={resourceLinks.session1Colab} target="_blank" rel="noreferrer">Notebook on Colab ↗</a>
                              ) : null}
                              {session.id === "2" ? (
                                <a className="readings-page-link" href={session2DeckPath}>Study deck →</a>
                              ) : null}
                              {session.readings.map((reading) => (
                                <a key={reading.href} href={reading.href} target="_blank" rel="noreferrer">
                                  {reading.title} ↗
                                </a>
                              ))}
                            </span>
                          </span>
                          <span className="curriculum-meta">{sessionDates(session.id)}<small>{session.hours}</small></span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </section>

        <section className="page-width materials-section" id="materials">
          <div className="section-intro">
            <p className="section-label">Materials</p>
            <div><h2>Where all the reading comes from</h2><p>Anthropic’s Transformer Circuits Thread is still the spine of the year. From Phase III on, the readings also draw on Anthropic’s Alignment Science blog, DeepMind and OpenAI work on interpretability and monitoring, and independent groups such as Transluce. Each session’s readings are linked in the plan above.</p></div>
          </div>
          <div className="resource-list">
            <ResourceRow label="READ" title="Transformer Circuits Thread" description="transformer-circuits.pub · the full research line, from the mathematical framework to attribution graphs" href="https://transformer-circuits.pub/" action="Start reading" />
            <ResourceRow label="READ" title="Anthropic Alignment Science blog" description="alignment.anthropic.com · auditing agents, activation oracles, Petri — the monitoring and audit half of the year" href="https://alignment.anthropic.com/" action="Start reading" />
            <ResourceRow label="TOOLS" title="Neuronpedia · Gemma Scope" description="Browse the pretrained SAEs, transcoders, and attribution graphs we use from Session 10 onward" href="https://www.neuronpedia.org/" action="Open Neuronpedia" />
            <ResourceRow label="DECK" title="Session 2 study deck" description="Mathematical Framework I: Zero & One Layer · 32 slides · path expansion, QK/OV circuits, skip-trigrams on attn-only-1l" href={session2DeckPath} action="Open the deck" />
            <ResourceRow label="DECK" title="Session 1 study deck" description="Environment, GPT-2 from Scratch & Why We’re Here · 32 slides · keyboard navigation · prints to PDF" href={session1DeckPath} action="Open the deck" />
            <ResourceRow label="COLAB" title="Session 1 notebook" description="GPT-2 from scratch, matched to TransformerLens layer by layer · a CPU runtime is enough" href={resourceLinks.session1Colab} action="Open in Colab" />
            <ResourceRow label="DECK" title="Session 0.5 study deck" description="Visual Circuits: Three Foundational Claims · 28 slides" href={deckPath} action="Open the deck" />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function SessionPage() {
  return (
    <>
      <Header onSessionPage />
      <main>
        <article>
          <header className="page-width article-header">
            <a className="breadcrumb" href="/">← Plan overview</a>
            <p className="kicker">Session 0.5 · Aug 30 – Sep 13, 2026</p>
            <h1>Visual Circuits:<br />Three Foundational Claims</h1>
            <p className="standfirst">Before we open up a transformer, we work out what a circuit explanation is actually supposed to deliver — and what kind of evidence it needs.</p>
            <div className="hero-links">
              <a className="text-button" href={deckPath}>Open the deck</a>
              <a href="#readings">Jump to the readings</a>
            </div>
            <div className="article-meta">
              <span><strong>11 hours</strong>7h read · 4h code</span>
              <span><strong>Kickoff</strong>Aug 30 · 9:00 PM ET</span>
              <span><strong>Debrief</strong>Sep 13 · 9:00 PM ET</span>
            </div>
          </header>

          <section className="page-width article-section">
            <div className="section-label">Purpose</div>
            <div className="article-copy">
              <h2>First, set the evidence standard.</h2>
              <p>The early Circuits work made three ambitious claims: neural networks learn meaningful <em>features</em>; features are connected by weights into reusable <em>circuits</em>; and related features and circuits may appear across models, with some degree of <em>universality</em>.</p>
              <p>This session is not about accepting those claims. It is about learning to inspect their evidence, reproduce one visualization with your own hands, and judge what would make the explanation stronger or weaker.</p>
            </div>
          </section>

          <section className="page-width article-section">
            <div className="section-label">Learning goals</div>
            <div className="article-copy">
              <ol className="numbered-copy">
                <li><span>1</span><p>Define feature and circuit clearly, with an example you can point to.</p></li>
                <li><span>2</span><p>Distinguish visual evidence, correlational evidence, and causal evidence.</p></li>
                <li><span>3</span><p>Explain why equivariance and recurring feature families matter for the universality claim.</p></li>
                <li><span>4</span><p>Make one CNN feature visualization yourself, recording every choice that shaped the result.</p></li>
                <li><span>5</span><p>Write a concise evidence audit stating both what the papers show and what remains unproven.</p></li>
              </ol>
            </div>
          </section>

          <section className="page-width article-section">
            <div className="section-label">The two weeks</div>
            <div className="article-copy schedule-list">
              <div><span>8.30 – 9.05</span><h3>Read & define</h3><p>Read the four core articles. Build and maintain a one-page glossary for feature, circuit, universality, equivariance, polysemanticity, and causal intervention.</p></div>
              <div><span>9.06 – 9.11</span><h3>Inspect & build</h3><p>Work through <code>00.5_inception_circuits.ipynb</code>: surface the mixed3b curve-detector family with synthetic curve stimuli, then read how 3b:379 is constructed from the multiplied-out weights.</p></div>
              <div><span>9.12 – 9.13</span><h3>Audit & synthesize</h3><p>Finish the evidence audit, pick two figures for discussion, and write down one claim you trust plus one claim you want to test next.</p></div>
            </div>
          </section>

          <section className="page-width article-section" id="readings">
            <div className="section-label">Core readings</div>
            <div className="article-copy reading-list">
              {sessionReadings.map((reading, index) => (
                <a href={reading.href} target="_blank" rel="noreferrer" key={reading.title}>
                  <span className="reading-number">{String(index + 1).padStart(2, "0")}</span>
                  <span><strong>{reading.title}</strong><small>{reading.authors}</small><p>{reading.description}</p></span>
                  <b>↗</b>
                </a>
              ))}
              <a href="https://distill.pub/2020/circuits/early-vision/" target="_blank" rel="noreferrer">
                <span className="reading-number">LAB</span>
                <span><strong>An Overview of Early Vision in InceptionV1</strong><small>Interactive feature catalog · stands in for the retired OpenAI Microscope</small><p>Browse the features and curve-detector families here before making your own visualization.</p></span>
                <b>↗</b>
              </a>
            </div>
          </section>

          <section className="page-width article-section">
            <div className="section-label">Output</div>
            <div className="article-copy">
              <h2>A one-page evidence audit</h2>
              <p>Organize the page under three headings: <strong>Features, Circuits, Universality.</strong> Under each, give the strongest evidence you found, one important limitation, and one experiment that would change your confidence.</p>
              <ul className="check-list">
                <li>One feature visualization of your own, with model, layer, channel, method, and hyperparameters recorded</li>
                <li>Two annotated figures from the readings</li>
                <li>A confidence score for each foundational claim</li>
                <li>A short answer: would reimplementing a circuit with handwritten weights work on a transformer?</li>
              </ul>
            </div>
          </section>

          <section className="expectation-callout">
            <div className="page-width article-section callout-inner">
              <div className="section-label">Come prepared</div>
              <div className="article-copy">
                <h2>At the September 13 meeting</h2>
                <p>Bring the audit and the visualization, even if they’re unfinished. Be ready to explain one methodological choice, one failure or ambiguity, and one question you want the group to discuss. The goal is a defensible learning artifact, not a polished research result.</p>
              </div>
            </div>
          </section>

          <section className="page-width article-section session-resources">
            <div className="section-label">Session materials</div>
            <div className="article-copy resource-list">
              <ResourceRow label="DECK" title="Session 0.5 study deck" description="28 slides · keyboard navigation · prints to PDF" href={resourceLinks.session05Deck} action="Open the deck" />
              <ResourceRow label="COLAB" title="00.5_inception_circuits.ipynb" description="Reproduce the InceptionV1 curve-detector family and circuit weights · GPU recommended" href={resourceLinks.inceptionColab} action="Open in Colab" />
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}

const isDeckPage = normalizedPath === deckPath;
const isSession1DeckPage = normalizedPath === session1DeckPath;
const isSession2DeckPage = normalizedPath === session2DeckPath;
const isSessionPage = normalizedPath === sessionPath;

document.title = isSession2DeckPage
  ? "Session 2 Deck · Mathematical Framework I: Zero & One Layer · NC AI Opsis"
  : isSession1DeckPage
  ? "Session 1 Deck · Environment, GPT-2 from Scratch & Why We’re Here · NC AI Opsis"
  : isDeckPage
    ? "Session 0.5 Deck · Visual Circuits · NC AI Opsis"
    : isSessionPage
      ? "Session 0.5 · Visual Circuits · NC AI Opsis"
      : "Reading the Inner Circuits of Language Models · NC AI Opsis";

const description = isSession2DeckPage
  ? "The Session 2 study deck (plan v2): the path expansion of a one-layer attention-only transformer, zero-layer bigram tables, QK and OV circuits, skip-trigrams and copying heads read off the weights of attn-only-1l, the keep-in-mind / at-bay symmetry bug head by head, instrument card #2, and the second AI audit round."
  : isSession1DeckPage
  ? "The Session 1 study deck (plan v2): microscope vs instrument, the residual stream as a bus, TransformerLens shape conventions, LayerNorm folding, the 2026 version landmines, the logit lens as the first instrument card, and the AI trial run — you write the spec, Claude Code writes GPT-2, you audit it."
  : isDeckPage
    ? "The Session 0.5 study deck: the three foundational claims of visual circuits, the evidence standard, the reading list, and the two-week plan."
    : isSessionPage
      ? "The Session 0.5 study plan: visual circuits, three foundational claims, readings, coding tasks, and the evidence audit."
      : "A public mechanistic interpretability study group: 54 weeks building a toolbox of instruments — probes, patching, SAEs, diffing, attribution graphs, audits — meeting every two weeks with artifacts in hand.";
document.querySelector('meta[name="description"]')?.setAttribute("content", description);

function Page() {
  if (isSession2DeckPage) return <Deck meta={session2Meta} slides={session2Slides} />;
  if (isSession1DeckPage) return <Deck meta={session1Meta} slides={session1Slides} />;
  if (isDeckPage) return <Deck meta={deckMeta} slides={session05Slides} />;
  if (isSessionPage) return <SessionPage />;
  return <HomePage />;
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode><Page /></React.StrictMode>,
);
